import React, { useEffect, useState } from "react";
import Tooltip from "@mui/material/Tooltip";
import { useHistory, useLocation, useParams } from "react-router-dom";

import PartyPerson from "./PartyPerson";
import PartyOrganisation from "./PartyOrganisation";
import PartyFamily from "./PartyFamily";
import PartySearch from "../../components/PartySearch";
import PageHeader from "../../../app/layout/PageHeader";

import PersonIcon from "@mui/icons-material/Person";
import BusinessIcon from "@mui/icons-material/Business";
import FamilyRestroomIcon from "@mui/icons-material/FamilyRestroom";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

import { PartySummaryDto, SelectedSession } from "../../types/types";
import { api } from "../../../api/apiClient";
import { useAuth } from "../../../context/AuthContext";
import { notify } from "../../components-ui/toast";

import "../../../css/forms.css";
import "../../../css/layout.css";
import "../../../css/buttons.css";
import "./PartyManagement.css";
import {
  adminPartyFormContext,
  PartyFormProvider,
} from "./PartyFormContext";

interface LocationState {
  returnTo?: string;
  session?: SelectedSession;
  partyId?: number;
  partyType?: PartyManagementTab;
  familyInitialStep?: number;
  registrationOrigin?: "dashboard" | "services";
  originServiceId?: number;
}

type PartyManagementTab = "Person" | "Family" | "Organisation";

interface RouteParams {
  partyId?: string;
}

const PartyManagement: React.FC = () => {
  const { canAccessAdmin } = useAuth();
  const [archiving, setArchiving] = useState(false);
  const history = useHistory();
  const location = useLocation<LocationState>();
  const { partyId: routePartyId } = useParams<RouteParams>();
  const returnTo = location.state?.returnTo;
  const session = location.state?.session;
  const queryParams = new URLSearchParams(location.search);
  const queryServiceId = Number(queryParams.get("serviceId"));
  const serviceRegistrationContext =
    Number.isFinite(queryServiceId) && queryServiceId > 0
      ? queryServiceId
      : undefined;
  const queryMemberType = queryParams.get("memberType") ?? "ServiceUser";
  const queryPartyType = queryParams.get("partyType");
  const registrationPartyType =
    queryPartyType === "Family" || queryPartyType === "Person"
      ? queryPartyType
      : undefined;
  const initialPartyId =
    location.state?.partyId ?? (routePartyId ? Number(routePartyId) : null);
  const initialPartyType =
    location.state?.partyType ??
    registrationPartyType ??
    (serviceRegistrationContext && queryMemberType === "ServiceUser"
      ? "Family"
      : "Person");
  const familyInitialStep = location.state?.familyInitialStep ?? 0;
  const [partyType, setPartyType] = useState<PartyManagementTab>(
    initialPartyType,
  );
  const [selectedPartyId, setSelectedPartyId] = useState<number | null>(
    initialPartyId,
  );
  const [resetSignal, setResetSignal] = useState(0);

  useEffect(() => {
    if (!routePartyId) return;

    const numericPartyId = Number(routePartyId);
    if (!Number.isFinite(numericPartyId) || numericPartyId <= 0) return;

    setSelectedPartyId(numericPartyId);

    if (location.state?.partyType) return;

    api<PartySummaryDto>(`/api/PartyQuery/${numericPartyId}/summary`, {
      suppressLoading: true,
      notifyError: false,
    })
      .then((summary) => {
        if (
          summary.partyType === "Person" ||
          summary.partyType === "Family" ||
          summary.partyType === "Organisation"
        ) {
          setPartyType(summary.partyType);
        }
      })
      .catch(() => {
        setPartyType("Person");
      });
  }, [location.state?.partyType, routePartyId]);

  /* =========================
     Handle Save
  ========================= */

  const handleSaved = (partyId: number) => {
    if (returnTo === "registration") {
      history.push("/party-service-registration", {
        session,
        partyId,
        registrationOrigin: location.state?.registrationOrigin,
        originServiceId: location.state?.originServiceId,
      });
    } else {
      setSelectedPartyId(partyId);
    }
  };

  /* =========================
     Cancel
  ========================= */

  const handleCancel = () => {
    if (returnTo === "registration") {
      history.push("/party-service-registration", {
        session,
        registrationOrigin: location.state?.registrationOrigin,
        originServiceId: location.state?.originServiceId,
      });
    } else if (returnTo?.startsWith("/admin/")) {
      history.push(returnTo);
    } else {
      history.goBack();
    }
  };

  const handleClearForm = () => {
    setSelectedPartyId(null);
    setResetSignal((prev) => prev + 1);
  };

  const archive = async () => {
    if (!selectedPartyId || !window.confirm(partyType === "Family"
      ? "Archive this family grouping? Individual people and their service enrollments will be retained."
      : partyType === "Organisation"
        ? "Archive this organisation? Its services and organisation access will be disabled; records and invoice history will be retained."
        : "Archive this party? Service participation and related application access will be disabled; history will be retained.")) return;
    setArchiving(true);
    try {
      let url = `/api/party-archives/parties/${selectedPartyId}`;
      if (partyType === "Family") {
        const context = await api<{ householdId?: number }>(`/api/Party/family-context/${selectedPartyId}`);
        if (!context.householdId) { notify.error("No family grouping was found."); return; }
        url = `/api/party-archives/families/${context.householdId}`;
      }
      await api(url, { method: "POST" });
      notify.success(partyType === "Family" ? "Family archived." : "Party archived.");
      handleClearForm();
    } catch {} finally { setArchiving(false); }
  };

  return (
    <PartyFormProvider
      value={{
        ...adminPartyFormContext,
        serviceId: serviceRegistrationContext,
        allowServiceSelection: !serviceRegistrationContext,
      }}
    >
    <div className="page-container">
      <PageHeader
        title="Party Management"
        subtitle="Manage people, households and organisations."
        actions={
          <div className="service-management-header-actions">
            {selectedPartyId && canAccessAdmin && <button type="button" className="link-button" disabled={archiving} onClick={archive}>{archiving ? "Archiving..." : "Archive"}</button>}
            <Tooltip title="Clear form">
              <button
                className="icon-button-muted-solid"
                onClick={handleClearForm}
                aria-label="Clear form"
              >
                <RestartAltIcon fontSize="small" />
              </button>
            </Tooltip>
            <Tooltip
              title={
                returnTo === "registration"
                  ? "Back to registrations"
                  : returnTo?.startsWith("/admin/duplicate-parties")
                    ? "Back to potential duplicates"
                  : "Back to dashboard"
              }
            >
              <button
                className="icon-button-muted-solid"
                onClick={() =>
                  returnTo === "registration"
                    ? handleCancel()
                    : history.push(returnTo?.startsWith("/admin/duplicate-parties") ? returnTo : "/dashboard")
                }
                aria-label={
                  returnTo === "registration"
                    ? "Back to registrations"
                    : returnTo?.startsWith("/admin/duplicate-parties")
                      ? "Back to potential duplicates"
                    : "Back to dashboard"
                }
              >
                <ArrowBackIcon fontSize="small" />
              </button>
            </Tooltip>
          </div>
        }
      />

      <div className="tab-button-row party-management-tabs">
        <button
          type="button"
          onClick={() => {
            setPartyType("Person");
            setSelectedPartyId(null);
          }}
          className={`party-management-tab ${
            partyType === "Person" ? "active" : ""
          }`}
        >
          <PersonIcon />
          <span>Person</span>
        </button>

        <button
          type="button"
          onClick={() => {
            setPartyType("Family");
            setSelectedPartyId(null);
          }}
          className={`party-management-tab ${
            partyType === "Family" ? "active" : ""
          }`}
        >
          <FamilyRestroomIcon />
          <span>Family</span>
        </button>

        <button
          type="button"
          onClick={() => {
            setPartyType("Organisation");
            setSelectedPartyId(null);
          }}
          className={`party-management-tab ${
            partyType === "Organisation" ? "active" : ""
          }`}
        >
          <BusinessIcon />
          <span>Organisation</span>
        </button>
      </div>

      <div className="card">
        {/* Search */}

        <div style={{ margin: "1rem 0" }}>
          <PartySearch
            onPartySelected={(party) => {
              if (!party) {
                setSelectedPartyId(null);
                return;
              }

              if (!(partyType === "Family" && party.partyType === "Person")) {
                setPartyType(party.partyType);
              }

              setSelectedPartyId(party.partyId);
            }}
          />
        </div>

        {/* Forms */}

        <div>
          {partyType === "Person" && (
            <PartyPerson
              partyId={selectedPartyId}
              resetSignal={resetSignal}
              onSaved={handleSaved}
              onCancel={handleCancel}
              showCancel={returnTo === "registration"}
            />
          )}

          {partyType === "Family" && (
            <PartyFamily
              partyId={selectedPartyId}
              initialStep={familyInitialStep}
              key={resetSignal}
            />
          )}

          {partyType === "Organisation" && (
            <PartyOrganisation
              partyId={selectedPartyId}
              resetSignal={resetSignal}
              onSaved={handleSaved}
              onCancel={handleCancel}
              showCancel={returnTo === "registration"}
            />
          )}
        </div>
      </div>
    </div>
    </PartyFormProvider>
  );
};

export default PartyManagement;
